import { memo } from "react";
import { Avatar, Badge, Skeleton } from "@mui/joy";
import {
  UserPresence,
  useUserAvatarByIdQuery,
} from "../__generated__/types-and-hooks";
import { useTranslation } from "../i18n";

export interface UserAvatarProps {
  userId: number;
  size: "sm" | "md" | "lg";
  online?: boolean;
}

const badgeColor = {
  [UserPresence.Online]: "success",
  [UserPresence.Away]: "danger",
  [UserPresence.Offline]: "neutral",
};

function UserAvatar({ userId, size }: UserAvatarProps) {
  const t = useTranslation();
  const userByIdQuery = useUserAvatarByIdQuery({ variables: { id: userId } });
  if (userByIdQuery.loading) {
    return (
      <Skeleton animation="wave" variant="circular" width={32} height={32} />
    );
  }

  const user = userByIdQuery.data?.user_by_id;
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || t("User");
  const presence: keyof typeof badgeColor =
    user?.presence || UserPresence.Offline;

  return (
    <Badge
      color={badgeColor[presence]}
      variant={user?.presence === UserPresence.Online ? "solid" : "soft"}
      size={size}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      badgeInset="4px -4px"
    >
      <Avatar size={size} alt={fullName} src={user?.picture ?? ""} />
    </Badge>
  );
}

export default memo(UserAvatar);
